import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useGetClubBoatsQuery,
  useDeleteClubBoatMutation,
} from '@/services/ClubBoatManagementService';
import { BoatResponse } from '@/types/boat-management.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import { useSnackbar } from 'notistack';
import { Statuses } from '@/constant';
import { formatFinnishNumber } from '@/utils';

interface ClubBoat {
  id: string;
  name: string;
  type: string;
  unit: 'm' | 'ft';
  length: string;
  width: string;
  draft: string;
  registrationNumber: string;
  status: string;
}

interface ClubBoatManagementTabProps {
  clubId?: string;
}

const formatBoatMeasure = (value: string, unit: 'm' | 'ft') => {
  const numericValue = Number.parseFloat(value || '0');

  return `${formatFinnishNumber(
    Number.isNaN(numericValue) ? 0 : numericValue,
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )} ${unit}`;
};

const ClubBoatManagementTab = ({ clubId }: ClubBoatManagementTabProps) => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    boatId: string | null;
  }>({ isOpen: false, boatId: null });

  const {
    data: boatsResponse,
    isLoading,
    isFetching,
    isError,
  } = useGetClubBoatsQuery(
    {
      club_id: clubId || '',
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue || '',
    },
    {
      skip: !clubId,
    }
  );

  const [deleteClubBoat] = useDeleteClubBoatMutation();

  useEffect(() => {
    if (boatsResponse) {
      handleTotalPages(boatsResponse.totalPages || 1);
      handleTotalItems(boatsResponse.totalItems || 0);
    }
  }, [boatsResponse, handleTotalPages, handleTotalItems]);

  const filteredData: ClubBoat[] = useMemo(() => {
    if (!boatsResponse?.data) return [];

    return boatsResponse.data.map(
      (boat: BoatResponse): ClubBoat => ({
        id: boat.id,
        name: boat.name,
        type: boat.type,
        unit: boat.unit as 'm' | 'ft',
        length: boat.length,
        width: boat.width,
        draft: boat.draft,
        registrationNumber: boat.registration_number,
        status: boat.status === String(Statuses.ACTIVE) ? 'Active' : 'Inactive',
      })
    );
  }, [boatsResponse]);

  const handleResetFilters = useCallback(() => {
    if (searchTerm) {
      resetSearch();
    }
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [resetSearch, searchTerm, setPagination]);

  const handleAddBoat = useCallback(() => {
    const params = new URLSearchParams();
    if (clubId) {
      params.append('clubId', clubId);
    }
    navigate(`/boats/create?${params.toString()}`);
  }, [clubId, navigate]);

  const handleViewBoat = useCallback(
    (boat: ClubBoat) => {
      const params = new URLSearchParams();
      if (clubId) {
        params.append('clubId', clubId);
      }
      navigate(`/boats/view/${boat.id}?${params.toString()}`);
    },
    [clubId, navigate]
  );

  const handleEditBoat = useCallback(
    (boat: ClubBoat) => {
      const params = new URLSearchParams();
      if (clubId) {
        params.append('clubId', clubId);
      }
      navigate(`/boats/edit/${boat.id}?${params.toString()}`);
    },
    [clubId, navigate]
  );

  const handleDeleteBoat = useCallback((boat: ClubBoat) => {
    setConfirmDeleteState({ isOpen: true, boatId: boat.id });
  }, []);

  const handleConfirmDelete = async () => {
    if (!confirmDeleteState.boatId) return;
    try {
      await deleteClubBoat(confirmDeleteState.boatId).unwrap();
      enqueueSnackbar('Boat deleted successfully', { variant: 'success' });
      setConfirmDeleteState({ isOpen: false, boatId: null });
    } catch {
      enqueueSnackbar('Failed to delete boat', { variant: 'error' });
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: ClubBoat) => row.name,
        id: 'name',
        header: 'Boat Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: ClubBoat } }) =>
          hasPermission('club_boat_management_read') ? (
            <button
              type="button"
              onClick={() => handleViewBoat(row.original)}
              className="text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`View ${row.original.name}`}
            >
              {row.original.name}
            </button>
          ) : (
            <span className="text-sm font-medium text-gray-900">
              {row.original.name}
            </span>
          ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: ClubBoat) => row.type,
        id: 'type',
        header: 'Boat Type',
        enableSorting: false,
        cell: ({ row }: { row: { original: ClubBoat } }) => (
          <span className="text-gray-800 font-normal">{row.original.type}</span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: ClubBoat) => row.length,
        id: 'length',
        header: 'Length',
        enableSorting: false,
        cell: ({ row }: { row: { original: ClubBoat } }) => (
          <span className="text-gray-800 font-normal">
            {formatBoatMeasure(row.original.length, row.original.unit)}
          </span>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        accessor: (row: ClubBoat) => row.width,
        id: 'width',
        header: 'Width',
        enableSorting: false,
        cell: ({ row }: { row: { original: ClubBoat } }) => (
          <span className="text-gray-800 font-normal">
            {formatBoatMeasure(row.original.width, row.original.unit)}
          </span>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        accessor: (row: ClubBoat) => row.draft,
        id: 'draft',
        header: 'Draft',
        enableSorting: false,
        cell: ({ row }: { row: { original: ClubBoat } }) => (
          <span className="text-gray-800 font-normal">
            {formatBoatMeasure(row.original.draft, row.original.unit)}
          </span>
        ),
        meta: { className: 'min-w-[120px]' },
      },
      {
        accessor: (row: ClubBoat) => row.registrationNumber,
        id: 'registrationNumber',
        header: 'Registered Number',
        enableSorting: false,
        cell: ({ row }: { row: { original: ClubBoat } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.registrationNumber}
          </span>
        ),
        meta: { className: 'min-w-[180px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: ClubBoat } }) => (
          <div
            className="flex items-center gap-2"
            role="group"
            aria-label={`Actions for ${row.original.name}`}
          >
            {hasPermission('club_boat_management_read') && (
              <button
                type="button"
                onClick={() => handleViewBoat(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View boat"
                aria-label={`View ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="eye"
                  className="text-gray-500"
                  aria-hidden="true"
                />
              </button>
            )}
            {hasPermission('club_boat_management_update') && (
              <button
                type="button"
                onClick={() => handleEditBoat(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Edit boat"
                aria-label={`Edit ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                  aria-hidden="true"
                />
              </button>
            )}
            {hasPermission('club_boat_management_delete') && (
              <button
                type="button"
                onClick={() => handleDeleteBoat(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Delete boat"
                aria-label={`Delete ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="trash"
                  className="text-red-500"
                  aria-hidden="true"
                />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[140px]' },
      },
    ],
    [hasPermission, handleViewBoat, handleEditBoat, handleDeleteBoat]
  );

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex-shrink-0" role="search">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-boats-club"
              aria-label="Search club boats"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
                aria-hidden="true"
              />
              <input
                id="search-boats-club"
                placeholder="Search boats"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm"
                aria-label="Search boats by name, type or registration number"
                role="searchbox"
              />
            </label>
          </div>
        </div>
        <div
          className="flex items-center gap-2"
          role="toolbar"
          aria-label="Boat management actions"
        >
          {searchTerm && (
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
              onClick={handleResetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              Clear Filters
            </button>
          )}
          {hasPermission('club_boat_management_create') && (
            <button
              type="button"
              onClick={handleAddBoat}
              className="btn btn-sm btn-light h-8 sm:h-10"
              aria-label="Add new boat"
            >
              Add Boat
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div className="text-center py-4">
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
            />
            <span className="ml-2 text-gray-700">Loading boats...</span>
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-600">
            Error loading boats. Please try again.
          </div>
        ) : (
          <DataGrid
            columns={columns}
            data={filteredData}
            rowSelect={false}
            loading={isLoading || isFetching}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              totalItems,
              totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            sorting={[{ id: 'name', desc: false }]}
            serverSide={true}
            onFilterReset={handleResetFilters}
            hasActiveFilters={!!searchTerm}
            aria-label="Club boat management table"
          />
        )}
        <ConfirmationDialog
          open={confirmDeleteState.isOpen}
          onClose={() => setConfirmDeleteState({ isOpen: false, boatId: null })}
          onConfirm={handleConfirmDelete}
          message="Are you sure you want to delete this boat?"
          cancelButtonText="No"
          confirmButtonText="Yes"
        />
      </div>
    </div>
  );
};

export { ClubBoatManagementTab };
